import { useState } from "react";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import { hideModal } from "actions/modal";
import { getLoading, getRoutinesLoading } from "selectors/commonSelectors";
import { TextInput, Label } from "ui/ecosystems/forms";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/modal/modal";
import type { ReduxRootState } from "types/redux-store";

type RoutineWithPrefix = { _PREFIX: string };

const EMPTY_ROUTINES: RoutineWithPrefix[] = [];

const ConfirmModal = ({
  message,
  messageJsx,
  title,
  confirmType = "success",
  confirmText = "Ok",
  handleConfirm,
  handleCancel,
  loadingRoutines = EMPTY_ROUTINES,
  disabled = false,
  cancelType = "alert_outline",
  cancelText = "Cancel",
  confirmationTextToEnter = null,
  confirmButtonProps,
}: {
  message: string;
  messageJsx?: React.ReactNode;
  title: string | React.ReactNode;
  confirmType?: "success" | "error";
  confirmText?: string;
  handleConfirm: () => void;
  handleCancel?: () => void;
  loadingRoutines?: RoutineWithPrefix[];
  disabled?: boolean;
  cancelType?: "alert_outline" | "default";
  cancelText?: string;
  confirmationTextToEnter?: string | null;
  confirmButtonProps?: React.ComponentProps<typeof Button>;
}) => {
  const dispatch = useDispatch();
  const isLoading = useSelector(getLoading);
  const isLoadingRoutines = useSelector((state: ReduxRootState) =>
    getRoutinesLoading(state, loadingRoutines)
  );
  const [confirmInput, setConfirmInput] = useState("");
  // in case we don't have a unique function for cancel
  if (!handleCancel) {
    handleCancel = () => dispatch(hideModal());
  }
  let isDisabled = isLoading || isLoadingRoutines || disabled;
  if (confirmationTextToEnter) {
    isDisabled = isDisabled || confirmationTextToEnter !== confirmInput;
  }

  // Detect destructive dialog pattern (Discard post/all posts)
  const isDestructiveDialog =
    typeof title === "string" && title.toLowerCase().includes("discard");

  return (
    <Modal
      show={true}
      onHide={() => dispatch(hideModal())}
      animation={false}
      className={isDestructiveDialog ? "max-w-[440px]" : undefined}
    >
      {isDestructiveDialog ? (
        <>
          <div className="flex flex-col items-center text-center">
            {/* Destructive icon */}
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <i className="fa-solid fa-trash-can text-red-500 text-lg" />
            </div>
            <h4 className="text-lg font-semibold text-neutral-900 mb-2">
              {title}
            </h4>
            {message && (
              <p className="font-inter text-sm text-neutral-600 mb-0">
                {typeof message === "string" ? message : message}
              </p>
            )}
            {messageJsx && (
              <div className="text-sm text-neutral-600">{messageJsx}</div>
            )}
          </div>
          <div className="flex items-center justify-end gap-3 mt-6">
            <Button
              variant="blue-secondary"
              size="md"
              onClick={handleConfirm}
              disabled={isDisabled}
              {...confirmButtonProps}
            >
              {confirmText}
            </Button>
            <Button variant="red-primary" size="md" onClick={handleCancel}>
              {cancelText}
            </Button>
          </div>
        </>
      ) : (
        <>
          <Modal.Header closeButton>
            <Modal.Title className="text-blue-700 font-semibold">
              {title}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="">
              {message && (
                <p className="font-(family-name:--font-inter)">{message}</p>
              )}
              {messageJsx && <>{messageJsx}</>}
              {confirmationTextToEnter && (
                <ConfirmationTextWrapper>
                  <ConfirmationTextLabel>
                    To confirm, enter <em>{confirmationTextToEnter}</em> in the
                    field below.
                  </ConfirmationTextLabel>
                  <ConfirmationFormInput
                    required={true}
                    autoFocus
                    placeholder={confirmationTextToEnter}
                    value={confirmInput}
                    onChange={setConfirmInput}
                    type={"text"}
                  />
                </ConfirmationTextWrapper>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant={
                cancelType === "alert_outline"
                  ? "red-primary"
                  : "blue-secondary"
              }
              size="md"
              onClick={handleCancel}
            >
              {cancelText}
            </Button>
            <Button
              variant={
                confirmType === "success" ? "blue-primary" : "red-primary"
              }
              size="md"
              onClick={handleConfirm}
              disabled={isDisabled}
              {...confirmButtonProps}
            >
              {confirmText}
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export default ConfirmModal;

const ConfirmationTextWrapper = styled.div`
  margin-top: 20px;
`;
const ConfirmationTextLabel = styled(Label)`
  font-size: 12px;
`;
const ConfirmationFormInput = styled(TextInput)`
  font-size: 12px;
  width: 50%;
`;
